import { useEffect, useState } from 'react';
import { getScheduledPaymentGroupPayments } from '@/services/batchPayment';
import { TBatchOverviewPayment } from '../types/ManagePayments';

const POLL_INTERVAL_MS = 5000;

// Session-scoped cache of the full payments list, keyed by group id. Only successful lookups are
// cached so reopening the Batch Overview modal for the same group skips the network call; errors
// are intentionally left uncached so they can be retried. While a group is being polled (see
// `isPolling` below) every tick refreshes this cache too, so it never serves a stale in-progress
// snapshot once the group finishes.
const paymentsCache = new Map<string, TBatchOverviewPayment[]>();

/**
 * Loads every payment in a scheduled payment group for the Batch Overview table. While the group
 * itself is IN_PROGRESS (`isPolling: true`), each individual payment's own outcome (succeeded/
 * failed) can keep changing as the batch is processed - the group-status poll driving the rest of
 * the list only carries aggregate counts, not per-payment detail, so this refetches the full list
 * on the same 5-second cadence to keep the per-row tick/cross icons current.
 *
 * `bypassCache` skips straight to a fresh fetch even if a cached snapshot exists (still populating
 * the cache from the result, so a later default-cached caller benefits too). Needed by BatchModal's
 * insufficient-funds inference: if this same group was polled earlier while IN_PROGRESS, the cache
 * can hold a pre-completion snapshot from up to POLL_INTERVAL_MS before the group actually finished
 * - stale enough to under-count which payments failed - so that read can't rely on the cache-first
 * path a plain display (like this table) is fine with.
 */
export const useBatchOverviewPayments = (
  groupId?: string,
  enabled = true,
  isPolling = false,
  bypassCache = false
) => {
  const [payments, setPayments] = useState<TBatchOverviewPayment[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!groupId || !enabled) {
      setPayments([]);
      return;
    }

    let isCancelled = false;

    const fetchPayments = (showLoading: boolean) => {
      if (showLoading) setIsLoading(true);
      return getScheduledPaymentGroupPayments(groupId)
        .then((result) => {
          paymentsCache.set(groupId, result);
          if (!isCancelled) setPayments(result);
        })
        .catch(() => {
          if (!isCancelled && !paymentsCache.has(groupId)) setPayments([]);
        })
        .finally(() => {
          if (!isCancelled && showLoading) setIsLoading(false);
        });
    };

    const cached = !bypassCache && paymentsCache.get(groupId);
    if (cached) {
      setPayments(cached);
    } else {
      fetchPayments(true);
    }

    const intervalId = isPolling ? setInterval(() => fetchPayments(false), POLL_INTERVAL_MS) : null;

    return () => {
      isCancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [groupId, enabled, isPolling, bypassCache]);

  return { payments, isLoading };
};
